import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Icon } from "@/components/Icon";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "@/components/Toast";
import { humanSize, timeAgo } from "@/lib/format";
import type { ProjectFileRow } from "@shared/types";

/**
 * /projects/$projectId/files — project Files tab (T49).
 *
 * Drag-and-drop (or click) uploads files scoped to the PROJECT (no task);
 * below, every file in the project: project files first, then task
 * attachments grouped under their task title. Rows support open (raw
 * redirect), inline rename, and delete.
 */

function uploadErrorCopy(code: string): string {
  switch (code) {
    case "mime_not_allowed":
      return "That file type isn't allowed.";
    case "size_invalid":
      return "File is too large (25 MB max).";
    default:
      return "Upload failed. Try again.";
  }
}

function FileRowItem({
  file,
  projectId,
}: {
  file: ProjectFileRow;
  projectId: string;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(file.filename);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: queryKeys.projectFiles(projectId) });
    qc.invalidateQueries({ queryKey: queryKeys.files() });
    if (file.task_id) {
      qc.invalidateQueries({
        queryKey: queryKeys.attachmentsForTask(file.task_id),
      });
    }
  };
  const del = useMutation({
    mutationFn: () => api.deleteAttachment(file.id),
    onSuccess: invalidate,
    onError: () => toast.error("Couldn't delete the file. Try again."),
    onSettled: () => setConfirming(false),
  });
  const rename = useMutation({
    mutationFn: (filename: string) => api.renameAttachment(file.id, filename),
    onSuccess: invalidate,
    onError: () => toast.error("Couldn't rename the file. Try again."),
    onSettled: () => setRenaming(false),
  });
  const commitRename = () => {
    const name = draft.trim();
    if (!name || name === file.filename) {
      setRenaming(false);
      setDraft(file.filename);
      return;
    }
    rename.mutate(name);
  };
  const isImage = file.mime_type.startsWith("image/");
  return (
    <div className="attach pf-row" data-testid={`project-file-${file.id}`}>
      <a
        className="a-link"
        href={`/api/attachments/${file.id}/raw`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {isImage ? (
          <img
            className="pf-thumb"
            src={`/api/attachments/${file.id}/raw`}
            alt=""
            loading="lazy"
          />
        ) : (
          <div className="a-ic">
            <Icon name="clip" size={15} />
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          {renaming ? (
            <input
              className="input pf-rename"
              value={draft}
              autoFocus
              onClick={(e) => e.preventDefault()}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") {
                  setRenaming(false);
                  setDraft(file.filename);
                }
              }}
              onBlur={commitRename}
              aria-label="File name"
            />
          ) : (
            <div className="a-nm">{file.filename}</div>
          )}
          <div className="a-meta">
            {file.uploader_name ?? file.uploaded_by} ·{" "}
            {timeAgo(file.uploaded_at)}
          </div>
        </div>
      </a>
      <span className="a-size">{humanSize(file.size_bytes)}</span>
      {!renaming && (
        <button
          type="button"
          className="a-del"
          aria-label={`Rename ${file.filename}`}
          title="Rename file"
          onClick={() => setRenaming(true)}
        >
          <Icon name="pencil" size={12} />
        </button>
      )}
      {confirming ? (
        <span className="a-del-confirm">
          <button
            type="button"
            className="a-del-yes"
            disabled={del.isPending}
            onClick={() => del.mutate()}
          >
            Delete
          </button>
          <button
            type="button"
            className="a-del-no"
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="a-del"
          aria-label={`Delete ${file.filename}`}
          title="Delete file"
          onClick={() => setConfirming(true)}
        >
          <Icon name="close" size={12} />
        </button>
      )}
    </div>
  );
}

function ProjectFilesPage() {
  const { projectId } = Route.useParams();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const q = useQuery({
    queryKey: queryKeys.projectFiles(projectId),
    queryFn: () => api.listProjectFiles(projectId),
  });

  async function uploadFiles(list: FileList | File[]) {
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        try {
          await api.uploadAttachment({ projectId }, file);
        } catch (e) {
          toast.error(
            `${file.name}: ${uploadErrorCopy(e instanceof ApiError ? e.code : "unknown")}`,
          );
        }
      }
      qc.invalidateQueries({ queryKey: queryKeys.projectFiles(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.files() });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const files = q.data?.files ?? [];
  const projectFiles = files.filter((f) => !f.task_id && !f.comment_id);
  const taskFiles = files.filter((f) => f.task_id || f.comment_id);
  const byTask = new Map<string, ProjectFileRow[]>();
  for (const f of taskFiles) {
    const key = f.task_title ?? "Other tasks";
    byTask.set(key, [...(byTask.get(key) ?? []), f]);
  }

  return (
    <div className="pg-wrap pf-page">
      <div
        className={`dropzone${dragOver ? " drag-over" : ""}`}
        role="button"
        tabIndex={0}
        aria-label="Upload files to this project"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length)
            void uploadFiles(e.dataTransfer.files);
        }}
      >
        <div className="dz-ic">
          <Icon name="clip" />
        </div>
        <div className="dz-t">
          {uploading ? "Uploading…" : "Drop files here or click to browse"}
        </div>
        <div className="dz-s">
          Stored with this project — PDF, images, docs, up to 25 MB each
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) void uploadFiles(e.target.files);
        }}
      />

      {q.isLoading ? (
        <Skeleton count={4} height={56} />
      ) : files.length === 0 ? (
        <EmptyState
          icon="folder"
          title="No files in this project yet"
          caption="Drop a file above, or attach one to a task."
        />
      ) : (
        <>
          <section aria-label="Project files">
            <h3 className="pf-group">
              Project files <span className="cnt">{projectFiles.length}</span>
            </h3>
            {projectFiles.length === 0 ? (
              <p className="t-sm t-muted">
                Nothing here yet — drop a file above.
              </p>
            ) : (
              projectFiles.map((f) => (
                <FileRowItem key={f.id} file={f} projectId={projectId} />
              ))
            )}
          </section>
          {[...byTask.entries()].map(([title, group]) => (
            <section key={title} aria-label={`Files on ${title}`}>
              <h3 className="pf-group">
                {title} <span className="cnt">{group.length}</span>
              </h3>
              {group.map((f) => (
                <FileRowItem key={f.id} file={f} projectId={projectId} />
              ))}
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/files")({
  component: ProjectFilesPage,
});
